import { type AxiosInstance } from 'axios';
import { storeToRefs } from 'pinia';
import { getEnvironmentVariables } from '@/helpers/auth';
import { getShellAccessTokenSilent, shellLoginRedirect } from '@/helpers/auth/useAuthShell';
import { useCurrentProjectStore } from '@/store/currentProjectStore';
import { createApiClient } from '@ics/auth-api';

const guidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function resolveProjectIdFromUrlQuery (): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const queryProjectId = new URLSearchParams(window.location.search).get('projectId');
  if (!queryProjectId) {
    return null;
  }

  const trimmed = queryProjectId.trim();
  return guidPattern.test(trimmed) ? trimmed : null;
}

function resolveProjectId (): string | null {
  const currentProjectStore = useCurrentProjectStore();
  const { projectId } = storeToRefs(currentProjectStore);
  return projectId.value || resolveProjectIdFromUrlQuery();
}

// Single-flight-guard for den sentrale 401-redirecten (WI 2220). Flere samtidige datakall som alle
// får 401 (utløpt token) skal lede til ÉN pålogging, ikke kolliderende redirect-/login-forsøk —
// nettopp kollisjonen denne buggen handlet om.
let unauthorizedRedirectInFlight = false;

async function handleUnauthorized (): Promise<void> {
  if (unauthorizedRedirectInFlight) {
    return;
  }
  unauthorizedRedirectInFlight = true;
  try {
    const intendedRoute = typeof window === 'undefined'
      ? undefined
      : `${window.location.pathname}${window.location.search}`;
    await shellLoginRedirect(intendedRoute);
  } catch (error) {
    console.error('[axiosClient] Sentral 401-redirect feilet:', error);
    unauthorizedRedirectInFlight = false;
  }
}

async function markServerVersionSeen (version: string): Promise<void> {
  try {
    const { useGlobalStore } = await import('@/store/storeGlobal');
    useGlobalStore().markVersionSeen(version);
  } catch (versionCheckError) {
    console.debug('[axiosClient] markVersionSeen feilet:', versionCheckError);
  }
}

// Cache for axios-instansen
let axiosClientInstance: AxiosInstance | null = null;
let initializationPromise: Promise<AxiosInstance> | null = null;

// Lazy-initialiseringsfunksjon
async function initializeAxiosClient (): Promise<AxiosInstance> {
  // Returner eksisterende instans hvis allerede initialisert
  if (axiosClientInstance) {
    return axiosClientInstance;
  }

  // Returner eksisterende promise hvis initialisering allerede pågår
  if (initializationPromise) {
    return initializationPromise;
  }

  // Opprett ny promise for initialisering
  initializationPromise = (async (): Promise<AxiosInstance> => {
    try {
      const config = await getEnvironmentVariables();

      const apiUrl = config?.VITE_API_URL;
      const baseURL = apiUrl?.startsWith('/') ? apiUrl : '/api';

      // Den autentiserte klienten (jwt-modus) via den felles fabrikken (WI 2220).
      // jwt = silent-only token-henting: et bakgrunns-datakall utløser ALDRI et påloggingsvindu
      // som sideeffekt. Mangler gyldig token sendes kallet uten Authorization → tjener svarer 401
      // → den sentrale 401-handleren leder forutsigbart til pålogging.
      const instance = createApiClient({
        mode: 'jwt',
        baseURL,
        getAccessTokenSilent: async () => (await getShellAccessTokenSilent())?.accessToken ?? null,
        getProjectId: resolveProjectId,
        onUnauthorized: handleUnauthorized,
        onServerVersion: markServerVersionSeen,
      });

      axiosClientInstance = instance;
      return instance;
    } finally {
      initializationPromise = null;
    }
  })();

  return initializationPromise;
}

const createAxiosClientProxy = (): AxiosInstance => {
  const wrapMethod = (methodName: string) => {
    return function (this: any, ...args: any[]) {
      if (axiosClientInstance) {
        const method = axiosClientInstance[methodName as keyof AxiosInstance] as any;
        return method.apply(axiosClientInstance, args);
      }

      return initializeAxiosClient().then((instance) => {
        const method = instance[methodName as keyof AxiosInstance] as any;
        return method.apply(instance, args);
      });
    };
  };

  return new Proxy({} as AxiosInstance, {
    get (target, prop: string | symbol) {
      const propName = String(prop);

      if (axiosClientInstance) {
        const value = axiosClientInstance[propName as keyof AxiosInstance];
        if (typeof value === 'function') {
          return value.bind(axiosClientInstance);
        }
        return value;
      }

      if (['get', 'post', 'put', 'delete', 'patch', 'head', 'options', 'request'].includes(propName)) {
        return wrapMethod(propName);
      }

      if (axiosClientInstance) {
        return axiosClientInstance[propName as keyof AxiosInstance];
      }

      return undefined;
    },
  });
};

export async function getAxiosClient (): Promise<AxiosInstance> {
  return await initializeAxiosClient();
}

export default createAxiosClientProxy();
